import {useCallback, useRef} from 'react';
import styled from 'styled-components';
import {Text} from '@forecast-it/design-system';
import {EmptyState} from './EmptyState';
import type {ActivityTimelineItem_activity$key} from './__generated__/ActivityTimelineItem_activity.graphql';
import {ActivityTimelineItem} from './ActivityTimelineItem';
import {useScrollToHash} from '../hooks/useScrollToHash';
import {useHashTracker} from '../hooks/useHashTracker';
import {useRetryMessage} from '../hooks/useRetryMessage';

interface Props {
	timeline: readonly ActivityTimelineItem_activity$key[];
	onRefetch?: () => void;
	/** Passed to each entry so an inbound email can name its sender. */
	requesterEmail?: string;
	/** Shown in place of the list when there is nothing to show. */
	emptyText?: string;
}

// A single line runs the length of the rail behind the entries' badges, so the
// history reads as one thread rather than a stack of rows. It stops short at both
// ends (the first and last badges cap it) and sits at z-index 0, under the badges'
// white discs.
const Rail = styled.div`
	position: relative;
`;
const RailLine = styled.div`
	position: absolute;
	left: 13px;
	top: 14px;
	bottom: 14px;
	width: 2px;
	z-index: 0;
	background: ${p => p.theme.sys.color.border.decorative};
`;
const Entries = styled.ol`
	position: relative;
	margin: 0;
	padding: 0;
	list-style: none;
	> li + li {
		margin-top: ${p => p.theme.sys.spacing[500]};
	}
`;

/**
 * The ticket's history as a vertical rail.
 *
 * Shared by the Activity tab and the overview's recent-activity block, so both
 * always render an entry the same way — three separate renderers of this data is
 * how they came to disagree in the first place.
 *
 * The caller decides what is in `timeline` and in what order: this draws the rail
 * and nothing else. Sorting and filtering belong to whoever owns the view.
 */
export function ActivityTimeline({timeline, onRefetch, requesterEmail, emptyText}: Props) {
	const containerRef = useRef<HTMLOListElement>(null);
	useScrollToHash();
	useHashTracker(containerRef);
	const {retry} = useRetryMessage();

	const handleRetry = useCallback(
		async (messageId: string) => {
			await retry(messageId);
			onRefetch?.();
		},
		[retry, onRefetch]
	);

	if (timeline.length === 0) {
		return (
			<EmptyState variant="inset">
				<Text color="auxiliary">{emptyText ?? 'No activity yet on this ticket.'}</Text>
			</EmptyState>
		);
	}

	return (
		<Rail>
			<RailLine aria-hidden="true" data-testid="activity-rail" />
			<Entries ref={containerRef} data-testid="activity-timeline">
				{timeline.map((item, i) => (
					<li key={i}>
						<ActivityTimelineItem activity={item} onRetry={handleRetry} requesterEmail={requesterEmail} />
					</li>
				))}
			</Entries>
		</Rail>
	);
}
